import { useEffect, useMemo } from 'react';
import {
  createBrowserRouter,
  createHashRouter,
  Navigate,
  Outlet,
  RouteObject,
  RouterProvider,
} from 'react-router-dom';

import GlobalModalsProvider from 'components/GlobalModalsProvider';
import SankeyIFramedPage from 'pages/SankeyIFramedPage';
import SignInWithEmailPage from 'pages/SignInWithEmailPage';

import { API, useApiContext } from '../../contexts/apiContext';
import { useAppEnvironmentContext } from '../../contexts/appEnvironmentContext';
import AddinHomePage from '../../pages/AddinHomePage';
import NotFoundPage from '../../pages/NotFoundPage';
import SankeyMainExamplePage from '../../pages/SankeyMainExamplePage';
import SankeyPage from '../../pages/SankeyPage';
import SankeyPreviewPage from '../../pages/SankeyPreviewPage';
import SankeysEditorRedirectPage from '../../pages/SankeysEditorRedirectPage';
import SankeysPage from '../../pages/SankeysPage';
import SignInGoogleRedirectPage from '../../pages/SignInGoogleRedirectPage';
import SignInMicrosoftRedirectPage from '../../pages/SignInMicrosoftRedirectPage';
import SignUpWithEmailPage from '../../pages/SignUpWithEmailPage';
import SubscriptionsCheckoutCanceledPage from '../../pages/SubscriptionsCheckoutCanceledPage';
import SubscriptionsCheckoutRedirectPage from '../../pages/SubscriptionsCheckoutRedirectPage';
import SubscriptionsCheckoutSuccessPage from '../../pages/SubscriptionsCheckoutSuccessPage';
import { getURLHashRoute, Path } from '../../utils/routes';
import CorporateBrandingProvider from '../CorporateBrandingProvider';
import PlatformInfoTrackers from '../PlatformInfoTrackers';
import SankeyAutosaveProvider from '../SankeyAutosaveProvider';
import SankeyExcelBindingProvider from '../SankeyExcelBindingProvider';
import SankeyFormProvider from '../SankeyFormProvider';
import SankeyRenderProvider from '../SankeyRenderProvider';

const buildRoutes = (api: API): RouteObject[] => [
  // Popup routes (not wrapped with PlatformInfoTrackers)
  {
    path: Path.SubscriptionsCheckoutRedirect,
    element: <SubscriptionsCheckoutRedirectPage />,
  },
  {
    path: Path.SubscriptionsCheckoutSuccess,
    element: <SubscriptionsCheckoutSuccessPage />,
  },
  {
    path: Path.SubscriptionsCheckoutCanceled,
    element: <SubscriptionsCheckoutCanceledPage />,
  },
  { path: Path.SignInGoogleRedirect, element: <SignInGoogleRedirectPage /> },
  {
    path: Path.SignInMicrosoftRedirect,
    element: <SignInMicrosoftRedirectPage />,
  },
  {
    path: Path.SignInWithEmail,
    element: <SignInWithEmailPage />,
  },
  {
    path: Path.SignUpWithEmail,
    element: <SignUpWithEmailPage />,
  },
  // App routes
  {
    path: Path.AddinHome,
    element: (
      <PlatformInfoTrackers>
        <Outlet />
      </PlatformInfoTrackers>
    ),
    errorElement: <NotFoundPage />,
    children: [
      {
        index: true,
        element: (
          <GlobalModalsProvider>
            <AddinHomePage />
          </GlobalModalsProvider>
        ),
      },
      {
        path: Path.Sankeys,
        errorElement: <NotFoundPage withHeader withFooter />,
        children: [
          {
            index: true,
            element: (
              <GlobalModalsProvider>
                <SankeysPage />
              </GlobalModalsProvider>
            ),
          },
          {
            path: Path.LegacySankeysOverview,
            element: <Navigate to={Path.Sankeys} />,
          },
          {
            path: Path.SankeysEditorRedirect,
            element: <SankeysEditorRedirectPage />,
          },
          {
            path: Path.SankeyMainExample,
            loader: async () => {
              const sankeysResponse = await api().sankeys.list({
                isMainExample: true,
              });
              const sankey = sankeysResponse.data[0];
              if (!sankey) {
                throw new Error('Main example Sankey not found');
              }
              return sankey;
            },
            element: <SankeyMainExamplePage />,
          },
          {
            path: Path.Sankey,
            element: (
              <SankeyFormProvider>
                <SankeyAutosaveProvider>
                  <SankeyRenderProvider>
                    <SankeyExcelBindingProvider>
                      <GlobalModalsProvider>
                        <CorporateBrandingProvider>
                          <Outlet />
                        </CorporateBrandingProvider>
                      </GlobalModalsProvider>
                    </SankeyExcelBindingProvider>
                  </SankeyRenderProvider>
                </SankeyAutosaveProvider>
              </SankeyFormProvider>
            ),
            children: [
              {
                index: true,
                element: <SankeyPage />,
              },
              {
                path: Path.SankeyPreview,
                element: <SankeyPreviewPage />,
              },
              {
                path: Path.SankeyIFramed,
                element: <SankeyIFramedPage />,
              },
            ],
          },
        ],
      },
      {
        path: Path.LegacyAddinSignIn,
        element: <Navigate to={Path.AddinHome} />,
      },
      {
        path: Path.LegacyAddinSignInWithPassword,
        element: <Navigate to={Path.AddinHome} />,
      },
      // If the user tries to access the add-in URL directly in the web browser,
      // redirect them to the hash value which is the add-in URL (since the add-in
      // is using hash routing via createHashRouter).
      {
        path: '/office/addin',
        element: <Navigate to={getURLHashRoute() || Path.Sankeys} />,
      },
    ],
  },
];

const AppRoutes: React.FC = () => {
  const { appEnvironment } = useAppEnvironmentContext();
  const { api } = useApiContext();

  // Build React routes based on the current environment and the API client
  const router = useMemo(() => {
    const routes = buildRoutes(api);
    return appEnvironment === 'web'
      ? createBrowserRouter(routes)
      : createHashRouter(routes);
  }, [appEnvironment, api]);

  // Keep track of the last visited page in the Office add-in document settings
  useEffect(() => {
    if (appEnvironment === 'office') {
      // Restore the last visited page on initial load
      const pathname = Office.context.document?.settings.get('pathname');
      if (pathname) {
        router.navigate(pathname);
      }
      // Save the current page to the document settings on every route change to be restored later
      router.subscribe(() => {
        const pathname = router.state.location.pathname;
        Office.context.document?.settings.set('pathname', pathname);
        Office.context.document?.settings.saveAsync();
      });
    }
  }, [appEnvironment]);

  return <RouterProvider router={router} />;
};

export default AppRoutes;
